import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useRewardTierApplied } from '@fhs/loyalty/src/hooks/use-reward-tier-applied';
import { useLoyaltyIncentivesAvailability } from '@fhs-legacy/frontend/src/pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import {
  CustomEventNames,
  EventTypes,
  useCRMEventsContext,
} from '@fhs-legacy/frontend/src/state/crm-events';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import {
  DateAndTimeErrors,
  IncentiveEvaluationErrorCodes,
} from '@fhs-legacy/frontend/src/state/loyalty/hooks/types';
import { getEvaluationResultMessage } from '@fhs-legacy/frontend/src/state/loyalty/hooks/utils/helpers';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { blockContentToPlainText } from '@fhs-legacy/frontend/src/utils/sanity';

interface UseOfferValidationProps {
  selectedOffer: any;
}

export function useOfferValidation({ selectedOffer }: UseOfferValidationProps) {
  /*
   * This hook checks offer availability, validates against cart rules, and determines
   * if the offer requirements are met. It returns isValid for styling of the cart-offer-item and gives
   * error messages.
   */
  const formatters = useIntl();
  const { formatMessage } = formatters;
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();
  const { calculatedCartTotal } = useOrderContext();
  const { logRBIEvent } = useCRMEventsContext();
  const loggedOfferRef = useRef<string | null>(null);
  const { rewardTierApplied } = useRewardTierApplied();

  const {
    offersFeedbackMap,
    offersWarningsMap,
    isDiscountNotApplied,
    incentiveSelections,
    offersLoadingState,
  } = useAppSelector(state => ({
    offersFeedbackMap: selectors.loyalty.selectOfferFeedbackMap(state),
    offersWarningsMap: selectors.loyalty.selectOffersWarningsMap(state),
    isDiscountNotApplied: selectors.loyalty.selectIsDiscountNotApplied(state),
    incentiveSelections: selectors.loyalty.selectSelectedOfferSelections(state),
    offersLoadingState: selectors.loyalty.selectOffersLoading(state),
  }));

  const [hasSeenEvaluation, setHasSeenEvaluation] = useState(false);

  const intl = useIntl();
  const hasReachedMaxRedemptionLimit = useAppSelector(
    selectors.loyalty.createHasReachedMaxRedemptionLimitSelector(intl)
  );

  useEffect(() => {
    setHasSeenEvaluation(false);
  }, [selectedOffer?._id]);

  useEffect(() => {
    if (!selectedOffer) {
      return;
    }

    if (offersLoadingState) {
      setHasSeenEvaluation(true);
      return;
    }

    const offerId = selectedOffer?.loyaltyEngineId || selectedOffer?._id;
    const hasFeedbackEntry = !!(
      offerId && Object.prototype.hasOwnProperty.call(offersFeedbackMap, offerId)
    );

    if (hasFeedbackEntry || isDiscountNotApplied) {
      setHasSeenEvaluation(true);
    }
  }, [offersLoadingState, offersFeedbackMap, isDiscountNotApplied, selectedOffer]);

  const isPendingValidation = !!selectedOffer && (offersLoadingState || !hasSeenEvaluation);

  useEffect(() => {
    if (isDiscountNotApplied && selectedOffer && loggedOfferRef.current !== selectedOffer._id) {
      loggedOfferRef.current = selectedOffer._id;
      logRBIEvent({
        name: CustomEventNames.DISCOUNT_NOT_APPLIED,
        type: EventTypes.Other,
        attributes: {
          offer: {
            id: selectedOffer._id,
            loyaltyEngineId: selectedOffer.loyaltyEngineId ?? '',
            name: blockContentToPlainText(selectedOffer?.name?.localeRaw),
          },
          cart_version: 'fresh',
        },
      });
    }
  }, [isDiscountNotApplied, selectedOffer, logRBIEvent]);

  const {
    isValid = true,
    textOfferRequirementsNotMet = 'Sorry there was an error with this offer',
    warningMessage,
  } = useMemo(() => {
    if (!selectedOffer) {
      return { isValid: true, textOfferRequirementsNotMet: '' };
    }

    const available = checkLoyaltyOfferAvailability(selectedOffer);
    const ruleErrors = offersFeedbackMap[selectedOffer.loyaltyEngineId || ''];

    const hasRuleErrors = Boolean(ruleErrors?.length);
    const isCartTotalNegative = calculatedCartTotal < 0;
    const isReward =
      selectedOffer?.incentives?.some(
        (incentive: { incentiveType?: string }) =>
          incentive?.incentiveType?.toLowerCase() === 'reward'
      ) ?? false;
    const hasRewardSelections =
      isReward &&
      !!rewardTierApplied &&
      (incentiveSelections?.some(s => s.incentiveId === rewardTierApplied.id) ?? false);
    const hasRewardError = hasReachedMaxRedemptionLimit && rewardTierApplied;

    let errorMessage: string | undefined;
    if (!available) {
      errorMessage = formatMessage({ id: 'offerNotAvailableInRestaurantPos' });
    } else if (isCartTotalNegative) {
      errorMessage = formatMessage({ id: 'cartOfferRequirementsNotMetError' });
    } else if (hasRuleErrors) {
      // Show first error, with specific message for time-related validation errors
      if (DateAndTimeErrors.has(ruleErrors[0]?.code as IncentiveEvaluationErrorCodes)) {
        errorMessage = formatMessage({ id: 'futureOrderingOfferValidationError' });
      } else {
        const result = getEvaluationResultMessage({
          evaluationResult: ruleErrors[0],
          formatters,
          incentiveType: 'Promotion',
        });
        errorMessage = typeof result === 'string' ? result : undefined;
      }
    } else if (!hasRewardSelections && isDiscountNotApplied) {
      errorMessage = formatMessage({ id: 'incentiveFeedbackCartRequirement' });
    } else if (hasRewardError) {
      errorMessage =
        'You have reached the maximum daily redemption limit for rewards. Please try to claim a reward again tomorrow.';
    }

    const isValidResult =
      available &&
      !hasRuleErrors &&
      !isCartTotalNegative &&
      !hasRewardError &&
      (hasRewardSelections || !isDiscountNotApplied);

    // Warnings are non-blocking: only show when there are no errors
    const offerWarnings = offersWarningsMap[selectedOffer.loyaltyEngineId || ''];
    const offerWarningMessage =
      isValidResult && offerWarnings?.length ? offerWarnings[0].message : undefined;

    return {
      isValid: isValidResult,
      textOfferRequirementsNotMet: errorMessage,
      warningMessage: offerWarningMessage,
    };
  }, [
    selectedOffer,
    checkLoyaltyOfferAvailability,
    offersFeedbackMap,
    offersWarningsMap,
    calculatedCartTotal,
    isDiscountNotApplied,
    formatMessage,
    formatters,
    rewardTierApplied,
    incentiveSelections,
    hasReachedMaxRedemptionLimit,
  ]);

  return {
    isValid,
    textOfferRequirementsNotMet,
    warningMessage,
    isPendingValidation,
  };
}
